import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle2, ArrowRight, Phone } from 'lucide-react';
import { Seo, breadcrumbSchema, faqSchema } from '../components/Seo';
import FaqAccordion from '../components/FaqAccordion';
import QuoteForm from '../components/QuoteForm';
import { SERVICES, getServiceBySlug } from '../data/services';
import { SITE } from '../data/siteConfig';

const EXTRA_LINKS = {
  'renovation-energetique': { path: '/renovation-energetique', nav: 'Rénovation énergétique' },
};

const FORM_WORK_TYPE = {
  'ravalement-facade-paris': 'Ravalement de façade',
  'isolation-thermique-exterieure-ite': 'Isolation thermique extérieure (ITE)',
  'renovation-tous-corps-etat': 'Rénovation tous corps d’état',
  'etancheite-toiture-terrasse': 'Étanchéité toiture / terrasse',
  'couverture-toiture': 'Couverture / toiture',
};

const relatedLink = (slug) => {
  const s = getServiceBySlug(slug);
  if (s) return { path: s.path, nav: s.nav };
  return EXTRA_LINKS[slug] || null;
};

const ServicePage = ({ data }) => {
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: data.h1,
    serviceType: data.badge,
    description: data.metaDescription,
    areaServed: SITE.areaServed.map((a) => ({ '@type': 'City', name: a })),
    provider: {
      '@type': 'GeneralContractor',
      name: SITE.legalName,
      telephone: SITE.phone,
      url: SITE.url,
      address: {
        '@type': 'PostalAddress',
        streetAddress: SITE.street,
        postalCode: SITE.postalCode,
        addressLocality: SITE.city,
        addressCountry: 'FR',
      },
    },
  };
  const breadcrumb = breadcrumbSchema([
    { name: 'Accueil', path: '/' },
    { name: data.nav, path: data.path },
  ]);

  const related = (data.related || []).map(relatedLink).filter(Boolean);

  return (
    <main className="pt-28" data-testid={`service-${data.slug}`}>
      <Seo
        title={data.metaTitle}
        description={data.metaDescription}
        path={data.path}
        image={data.image}
        jsonLd={[serviceSchema, faqSchema(data.faq), breadcrumb]}
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-slate-900">
        <div className="absolute inset-0">
          <img src={data.image} alt={data.h1} className="w-full h-full object-cover opacity-25" />
        </div>
        <div className="container mx-auto px-4 relative z-10 py-20 md:py-28">
          <nav className="text-sm text-slate-300 mb-5" aria-label="Fil d’Ariane">
            <Link to="/" className="hover:text-emerald-300">Accueil</Link>
            <span className="mx-2 text-slate-500">/</span>
            <span className="text-slate-200">{data.nav}</span>
          </nav>
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 bg-emerald-600/20 border border-emerald-500/30 rounded-full px-4 py-2 text-emerald-200 text-sm font-medium mb-6">
              {data.badge}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">{data.h1}</h1>
            <p className="text-lg md:text-xl text-slate-200 leading-relaxed mb-8">{data.heroIntro}</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="#devis"
                data-testid="service-hero-cta"
                className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-700 hover:to-cyan-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all hover:gap-3 shadow-xl"
              >
                Demander un devis gratuit <ArrowRight className="h-5 w-5" />
              </a>
              <a
                href={`tel:${SITE.phone}`}
                className="inline-flex items-center justify-center gap-2 bg-white/10 border border-white/20 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white/20 transition-colors"
              >
                <Phone className="h-5 w-5" /> {SITE.phoneDisplay}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Sections */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto space-y-14">
            {data.sections.map((sec, i) => (
              <div key={i} data-testid={`service-section-${i}`}>
                <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">{sec.h2}</h2>
                <p className="text-lg text-slate-600 leading-relaxed mb-5">{sec.body}</p>
                {sec.items && (
                  <ul className="grid sm:grid-cols-2 gap-3">
                    {sec.items.map((it, j) => (
                      <li key={j} className="flex items-start gap-3">
                        <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-1" />
                        <span className="text-slate-700">{it}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8">Questions fréquentes</h2>
            <FaqAccordion items={data.faq} testId={`faq-${data.slug}`} />
          </div>
        </div>
      </section>

      {/* Related services */}
      {related.length > 0 && (
        <section className="py-14 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl font-bold text-slate-900 mb-6">Prestations associées</h2>
              <div className="grid sm:grid-cols-3 gap-4">
                {related.map((r) => (
                  <Link
                    key={r.path}
                    to={r.path}
                    data-testid={`related-${r.path}`}
                    className="flex items-center justify-between gap-3 border border-slate-200 rounded-xl px-5 py-4 hover:border-emerald-500 hover:text-emerald-700 transition-colors group"
                  >
                    <span className="font-medium text-slate-800 group-hover:text-emerald-700">{r.nav}</span>
                    <ArrowRight className="h-4 w-4 text-emerald-600" />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Quote form */}
      <section id="devis" className="py-16 md:py-20 bg-slate-50 scroll-mt-28">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">Demander un devis gratuit</h2>
            <p className="text-slate-600 mb-8">
              Décrivez votre projet et joignez vos photos : nous revenons vers vous rapidement pour une étude gratuite.
            </p>
            <Card className="border-slate-200 shadow-xl bg-white">
              <CardHeader>
                <CardTitle className="text-xl text-slate-900">{data.nav} — votre projet</CardTitle>
              </CardHeader>
              <CardContent>
                <QuoteForm defaultWorkType={FORM_WORK_TYPE[data.slug]} />
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ServicePage;
export { SERVICES };
